const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// After signOut, redirect back to the School-Admin sign-in page (relative
// path — Clerk handles the navigation).
const POST_LOGOUT_URI = '/signin';

type GetAccessToken = () => Promise<string | null | undefined>;
type SignOut = (options?: { redirectUrl?: string }) => Promise<void>;

let _getAccessToken: GetAccessToken | null = null;
let _signOut: SignOut | null = null;

/**
 * Called once from a React component to wire up Clerk's `getToken` function
 * so plain service modules can obtain Bearer tokens without hooks.
 */
export function setAccessTokenGetter(fn: GetAccessToken) {
  _getAccessToken = fn;
}

/**
 * Called once from a React component to wire up Clerk's `signOut` function
 * so the retry wrapper can sign out on persistent auth failure.
 */
export function setSignOutFunction(fn: SignOut) {
  _signOut = fn;
}

/**
 * Authenticated fetch wrapper. Attaches `Authorization: Bearer <token>` from
 * the Clerk session. Token-fetch failures are non-fatal (the request just
 * goes out unauthenticated and the server's auth guard returns 401).
 *
 * @param path  path relative to API_BASE_URL, e.g. "/v1/students"
 * @param init  standard RequestInit
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);

  if (_getAccessToken) {
    try {
      const token = await _getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } catch {
      // Token fetch failed — proceed without auth header
    }
  }

  const hasBody = init.body !== undefined && init.body !== null;
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;

  if (hasBody && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(`${API_BASE_URL}${path}`, { ...init, headers });
}

/**
 * Authenticated fetch wrapper with one auto-retry on 401/403.
 *
 * Clerk's `getToken()` caches and auto-refreshes; calling again on a fresh
 * tick triggers a refresh if the cached token is expiring. If the retry
 * also fails with 401 and we're not already on the sign-in or invite page,
 * sign the user out and redirect back to /signin.
 *
 * 403s are returned to the caller, never converted to a signout. A 403 with
 * a valid session means "authenticated but not allowed" — usually a
 * rank-gated endpoint (e.g. a CLASSROOM_VIEWER teacher hitting an ADMIN+
 * action), which the page should surface inline. Ejecting on 403 is how
 * every invited teacher used to get signed out on login when the dashboard
 * fired admin-tier fetches. The "wrong portal" case (a student with a valid
 * session on this app, where every request 403s) is handled by the
 * identity-based check in portalRole.ts, run by SignIn + ProtectedRoute —
 * not inferred here from response statuses.
 */
export async function apiFetchWithRetry(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const response = await apiFetch(path, init);

  if (response.ok || (response.status !== 401 && response.status !== 403)) {
    return response;
  }

  console.debug(`[apiFetchWithRetry] ${response.status} detected, attempting token refresh...`);

  if (!_getAccessToken) {
    console.error('[apiFetchWithRetry] getAccessToken not available, cannot retry');
    return response;
  }

  try {
    const freshToken = await _getAccessToken();

    if (!freshToken) {
      console.debug('[apiFetchWithRetry] Token refresh returned nothing — letting caller handle');
      return response;
    }

    const retryHeaders = new Headers(init.headers);
    retryHeaders.set('Authorization', `Bearer ${freshToken}`);

    const hasBody = init.body !== undefined && init.body !== null;
    const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;

    if (hasBody && !isFormData && !retryHeaders.has('Content-Type')) {
      retryHeaders.set('Content-Type', 'application/json');
    }

    const retryResponse = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: retryHeaders,
    });

    if (retryResponse.status === 401) {
      // A 401 that survives a token refresh means the session is genuinely
      // dead — sign out. Suppress during /signin (Clerk handles its own
      // flow there) and /invite/* (the invite accept page may race with
      // JIT provisioning on first sign-in; let it retry rather than
      // bouncing the user back out).
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const inAuthFlow = path.startsWith('/signin') || path.startsWith('/invite/');
      if (!inAuthFlow) {
        console.debug('[apiFetchWithRetry] Retry failed with 401 — signing out');
        await new Promise((resolve) => setTimeout(resolve, 100));
        await _signOut?.({ redirectUrl: POST_LOGOUT_URI });
      }
    }

    return retryResponse;
  } catch (error) {
    console.error('[apiFetchWithRetry] Error during token refresh:', error);
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const inAuthFlow = path.startsWith('/signin') || path.startsWith('/invite/');
    if (!inAuthFlow) {
      await _signOut?.({ redirectUrl: POST_LOGOUT_URI });
    }
    return response;
  }
}
